import React, { useCallback, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { authService } from '../services/api';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useModalA11y } from '../hooks/useModalA11y';
import { PrimaryButton } from './PrimaryButton';

// Listens for the global 'owndecks:email-unverified' event (dispatched
// by the axios interceptor when any write endpoint returns 403 with
// code 'email_unverified') and shows a friendly modal asking the user
// to verify their email — with a 'Resend verification link' button —
// instead of leaving the calling component with an opaque error.

export const UnverifiedActionModal = () => {
  const { user } = useContext(AuthContext);
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const close = useCallback(() => { setOpen(false); setSent(false); }, []);
  const dialogRef = useModalA11y(close, open);

  // Reset `sent` on each refire so a second 403 after a successful
  // resend doesn't reopen the modal still showing the green "link on
  // its way" state from the previous round. Close already resets it
  // too — this covers the case where the modal stayed open through a
  // route change (it's mounted at the App root and never unmounts).
  useEffect(() => {
    const handler = () => { setOpen(true); setSent(false); };
    window.addEventListener('owndecks:email-unverified', handler);
    return () => window.removeEventListener('owndecks:email-unverified', handler);
  }, []);

  const resend = async () => {
    setBusy(true);
    try {
      await authService.resendVerification();
      setSent(true);
    } catch (e) {
      showToast(e.response?.data?.error || 'Could not resend right now.', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
      onClick={close}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="unverified-modal-title"
        className="bg-night-50 border border-line rounded-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="unverified-modal-title" className="text-xl font-bold text-fg mb-2 tracking-tight">Verify your email first</h2>
        <p className="text-sm text-fg-muted mb-5">
          To keep the scene safe, posting and booking actions are reserved for
          verified accounts. We sent a link to <strong className="text-fg">{user?.email || 'your email'}</strong>{' '}
          when you signed up — open it to unlock these features.
        </p>
        {sent ? (
          <p className="text-sm text-green-300 bg-green-500/10 border border-green-500/30 rounded p-3 mb-4">
            New verification link on its way. Check your inbox (and spam folder).
          </p>
        ) : (
          <PrimaryButton
            onClick={resend}
            disabled={busy}
            className="w-full py-2.5 mb-3"
          >
            {busy ? 'Sending…' : 'Resend verification link'}
          </PrimaryButton>
        )}
        <button
          onClick={close}
          className="w-full px-4 py-2 text-sm text-fg-muted hover:text-fg"
        >
          Close
        </button>
      </div>
    </div>,
    document.body,
  );
};
